import { ITranslation } from 'types/Translation';
import { IGridData } from 'types/models';

export type IdsStatus = 'full' | 'partial' | 'atomic' | 'atomic-gf' | 'resolved-gf' | 'none';
export type Role = 'semantic' | 'phonetic' | 'none';

/** Row of the Characters grid. `id` is the code point (or GF_ID_BASE + seq for gf-<seq>). */
export type CardRow = IGridData & {
  cardId: string;
  chr: string | null;
  gfSeq: number | null;
  gfName: string | null;
  gfLabel: string;
  ids: string | null;
  idsStatus: IdsStatus;
  radical: string | null;
  strokeCount: number;
  componentOnly: boolean;
  inStandard: boolean;
  complete: boolean;
  gloss: string | null;
  internalName: string | null;
  origin: 'makemeahanzi' | 'synthetic' | 'none';
  referenceHost: string | null;
  /** direct pieces, space separated */
  pieces: string;
  /** GF leaves in order, space separated; only in lists */
  baseComponents?: string;
  usedBy: number;
};

export type Stroke = { order: number; path: string; median: [number, number][]; piece: number | null };

export type PieceChild = {
  id: string;
  chr: string | null;
  gloss: string | null;
  internalName: string | null;
  gfSeq: number | null;
  gfName: string | null;
  exists: boolean;
};

export type Piece = {
  idx: number;
  childId: string | null;
  /** IDS operator + slot per level, e.g. "⿰1/⿱0" */
  position: string | null;
  role: Role;
  resolvedByGf: boolean;
  child: PieceChild | null;
  /** flat view only: intermediate pieces this one came through (草 › 早 › 日) */
  via?: string[];
};

/** Flat view down to the components of the norm, with the strokes redistributed. */
export type FlatView = { pieces: Piece[]; strokePieces: (number | null)[]; expanded: boolean };

export type Card = Omit<CardRow, 'pieces' | 'baseComponents'> & {
  radicalIndex: number | null;
  referenceStrokes: number[] | null;
  gfGroup: number | null;
  updatedAt: string;
  strokes: Stroke[];
  pieces: Piece[];
  usedIn: string[];
  base: FlatView;
};

/** What the item pane edits: the card plus display strings for the read-only fields. */
export type CardItem = Card & {
  missing?: false;
  /** names of the component for the learner (translations, ref 112); only in the Components tab */
  sources?: ITranslation[];
  structureLabel: string;
  typeLabel: string;
  radicalLabel: string;
  drawnFrom: string;
  flags: string;
};

export type MissingItem = {
  id: number;
  cardId: string;
  missing: true;
  gfSeq?: number | null;
  sources?: ITranslation[];
};

export type CardsPage = { page: number; limit: number; total: number; rows: CardRow[] };

/** Row of the Components grid. `id` is the GF number. */
export type ComponentRow = IGridData & {
  seq: number;
  group: number;
  name: string;
  glyph: string | null;
  bareStroke: boolean;
  cardId: string | null;
  strokeCount: number | null;
  complete: boolean | null;
  origin: string | null;
  referenceHost: string | null;
  internalName: string | null;
  inStandard: boolean | null;
  usedBy: number;
};

/** Row of the Wizard grid. `id` is the code point. */
export type ScanRow = IGridData & {
  chr: string;
  voc: number;
  item: number;
  compound: number;
  culturalVoc: number;
  total: number;
  hasCard: boolean;
  complete: boolean | null;
  componentOnly: boolean | null;
};

export type ScanResult = {
  dialectId: number;
  summary: { total: number; missing: number; incomplete: number; ok: number };
  rows: ScanRow[];
};

export type PopulateReport = {
  requested: number;
  closure: number;
  toBuild: number;
  built: number;
  skippedExisting: number;
  synthetic: number;
  syntheticWithoutHost: string[];
  noData: string[];
  incomplete: string[];
  applied: boolean;
};

export type Stats = {
  cards: number;
  inStandard: number;
  componentOnly: number;
  complete: number;
  incomplete: number;
  synthetic: number;
  noData: number;
  strokes: number;
  strokesWithoutPiece: number;
  pieces: number;
  piecesUnresolved: number;
  components: number;
  componentsWithCard: number;
  byStatus: Record<string, number>;
};

export const STATUS_LABELS: Record<IdsStatus, string> = {
  full: 'Full',
  partial: 'Partial',
  atomic: 'Atomic',
  'atomic-gf': 'Atomic (GF component)',
  'resolved-gf': 'Resolved by GF',
  none: 'No data',
};

export const ROLE_COLORS: Record<Role, string> = { semantic: '#2563eb', phonetic: '#dc2626', none: '#6b7280' };
export const PIECE_COLORS = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#be185d', '#4d7c0f'];
export const UNASSIGNED_COLOR = '#c4b5fd';

/** Synthetic components have no code point: gf-<seq> ↔ GF_ID_BASE + seq (same rule as the backend). */
export const GF_ID_BASE = 2000000;
export const cardIdFromNum = (n: number): string =>
  n >= GF_ID_BASE ? `gf-${n - GF_ID_BASE}` : String.fromCodePoint(n);
export const numFromCardId = (id: string): number =>
  id.startsWith('gf-') ? GF_ID_BASE + parseInt(id.slice(3), 10) : (id.codePointAt(0) as number);
